/**
 * Query keys: Escalations, Service Requests, Reviews, Actions, Announcement Response, Message Deliverability dashboards.
 *
 * Keys are arrays that start with a stable domain prefix so `invalidateQueries`
 * can target a whole domain by prefix. Put every input the response depends on
 * (account, user, portfolio ids, filters, date range) into the key.
 *
 * Every dashboard factory takes one params object: `accountId`, `portfolioIds`,
 * and the request's own inputs (days, startDate/endDate, filters, limit, …).
 * `insightsOpsParams` normalizes it so the server's portfolio set is keyed by a
 * sorted copy; callers still send their original order to the server.
 */

const sortPortfolioIds = (portfolioIds) => {
  const ids = Array.isArray(portfolioIds)
    ? portfolioIds
    : String(portfolioIds ?? '')
        .split(',')
        .filter(Boolean)
  return ids.map(String).sort((left, right) => Number(left) - Number(right) || left.localeCompare(right))
}

/** Key-safe copy of a dashboard request scope: sorted portfolio ids, no undefined values. */
export const insightsOpsParams = ({ accountId = null, portfolioIds, ...rest } = {}) => {
  const params = { accountId, portfolioIds: sortPortfolioIds(portfolioIds) }
  Object.entries(rest).forEach(([name, value]) => {
    if (value !== undefined) params[name] = value
  })
  return params
}

const scopedKey = (prefix, name) => (params) => [...prefix, name, insightsOpsParams(params)]

const ESCALATIONS = ['insightsOps', 'escalations']
const SERVICE_REQUESTS = ['insightsOps', 'serviceRequests']
const REVIEWS = ['insightsOps', 'reviews']
const ANNOUNCEMENTS = ['insightsOps', 'announcements']
const DELIVERABILITY = ['insightsOps', 'deliverability']

export const insightsOpsKeys = {
  all: ['insightsOps'],

  escalations: {
    all: ESCALATIONS,
    /** GET /api/escalations/dashboard/metrics — every caller, any param mix. */
    metricsAll: [...ESCALATIONS, 'metrics'],
    metrics: scopedKey(ESCALATIONS, 'metrics'),
    trends: scopedKey(ESCALATIONS, 'trends'),
    byPortfolio: scopedKey(ESCALATIONS, 'by-portfolio'),
    topics: scopedKey(ESCALATIONS, 'topics'),
    reasons: scopedKey(ESCALATIONS, 'reasons'),
    /** GET /api/escalations/dashboard/list — every caller, any filter. */
    listAll: [...ESCALATIONS, 'list'],
    list: scopedKey(ESCALATIONS, 'list'),
    faq: scopedKey(ESCALATIONS, 'faq'),
    reasonVisualization: scopedKey(ESCALATIONS, 'reason-visualization'),
    /** Resident or leasing response summary for the Escalations overview. */
    overviewAll: [...ESCALATIONS, 'overview'],
    overview: scopedKey(ESCALATIONS, 'overview'),
    /** Resident + leasing daily trend series for the source breakdown chart. */
    sourceTrends: scopedKey(ESCALATIONS, 'source-trends'),
  },

  // serviceRequests: section owned by the Service Requests migration.
  serviceRequests: {
    all: SERVICE_REQUESTS,
    /** POST /api/service-requests/dashboard/jobs + its status poll. */
    dashboardJob: scopedKey(SERVICE_REQUESTS, 'dashboard-job'),
    /** GET /api/service-requests/dashboard/open-over-7-days: `portfolioId` or `portfolioIds`. */
    openOverSevenDays: scopedKey(SERVICE_REQUESTS, 'open-over-7-days'),
    /** GET /api/service-requests/dashboard/work-order-attribution (cursor pages share one key). */
    workOrderAttribution: scopedKey(SERVICE_REQUESTS, 'work-order-attribution'),
    /** GET /api/service-requests/dashboard/property/:portfolioId/breakdown */
    propertyBreakdown: scopedKey(SERVICE_REQUESTS, 'property-breakdown'),
    /** GET /api/service-requests/dashboard/raw-data */
    rawData: scopedKey(SERVICE_REQUESTS, 'raw-data'),
    /**
     * GET /api/service-requests/dashboard/theme-responses: one page of negative replies
     * for a feedback theme. `category`, `limit`, `offset`, and `days` or `startDate`/`endDate`.
     */
    themeResponses: scopedKey(SERVICE_REQUESTS, 'theme-responses'),
  },

  // reviews: section owned by the Reviews migration.
  reviews: {
    all: REVIEWS,
    /**
     * POST /api/reviews/dashboard/jobs + its status poll. Params are the job payload:
     * `endpoint` ('metrics' | 'ranking' | 'discover'), `portfolioIds`, `days`, `startDate`, `endDate`,
     * and the discover inputs. Every caller of one endpoint with the same inputs shares the job.
     */
    dashboardJob: scopedKey(REVIEWS, 'dashboard-job'),
    /** GET /api/reviews/dashboard/timeline: `days`, `granularity`, `startDate`, `endDate`. */
    timeline: scopedKey(REVIEWS, 'timeline'),
    /** GET /api/reviews/dashboard/distribution */
    distribution: scopedKey(REVIEWS, 'distribution'),
    /** GET /api/reviews/dashboard/residesk-activity */
    resideskActivity: scopedKey(REVIEWS, 'residesk-activity'),
    /** GET /api/reviews/dashboard/review-link-conversions */
    linkConversions: scopedKey(REVIEWS, 'review-link-conversions'),
    /** GET /api/reviews/dashboard/review-link-conversations: `limit`. */
    linkConversations: scopedKey(REVIEWS, 'review-link-conversations'),
    /** GET /api/reviews/dashboard/review-link-conversion-matches: `limit`. */
    conversionMatches: scopedKey(REVIEWS, 'review-link-conversion-matches'),
    verificationCandidates: scopedKey(REVIEWS, 'verification-candidates'),
    /** POST/GET /api/reviews/dashboard/attribution-rematch: stored matching status and decisions. */
    attributionRematch: scopedKey(REVIEWS, 'attribution-rematch'),
    /** GET /api/reviews/dashboard/conversation-topics: `days` only (no date range). */
    conversationTopics: scopedKey(REVIEWS, 'conversation-topics'),
    /** GET /api/reviews/dashboard/highlighted: `limit`. */
    highlighted: scopedKey(REVIEWS, 'highlighted'),
    /** GET /api/reviews/dashboard/table: paging, sort, `search`, `ratingFilter`. */
    table: scopedKey(REVIEWS, 'table'),
    /** GET /api/reviews/dashboard/top-tags: `limit`, `minRating`, `days` or `startDate`/`endDate`. */
    topTags: scopedKey(REVIEWS, 'top-tags'),
  },

  // announcements: section owned by the Announcement Response migration.
  announcements: {
    all: ANNOUNCEMENTS,
    /** GET /api/announcements/response-insights/runs */
    runs: scopedKey(ANNOUNCEMENTS, 'runs'),
    /** POST /api/announcements/response-insights/jobs + its status poll: `announcementId`, `replyWindowDays`. */
    analysisJob: scopedKey(ANNOUNCEMENTS, 'analysis-job'),
  },

  // deliverability: section owned by the Message Deliverability migration.
  deliverability: {
    all: DELIVERABILITY,
    /** POST /api/message-deliverability/dashboard/deliverability/jobs + its status poll: `daysWindow`. */
    deliverabilityJob: scopedKey(DELIVERABILITY, 'deliverability-job'),
    /** GET /api/message-deliverability/dashboard/health-checks: `daysWindow`. */
    healthChecks: scopedKey(DELIVERABILITY, 'health-checks'),
    /** POST /api/insights/daily-message-counts/jobs + its status poll: `days`. */
    messageVolumeJob: scopedKey(DELIVERABILITY, 'message-volume-job'),
    /**
     * POST /api/message-deliverability/dashboard/failed-threads (a read: thread lookup by phone).
     * `portfolioIds: [portfolioId]` and `phoneNumbers` in request order.
     */
    failedThreads: scopedKey(DELIVERABILITY, 'failed-threads'),
  },
}
